import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { usersAPI, filesPlatformAPI, peoplePlatformAPI, positionsAPI } from '@/services';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../contexts/AuthContext';
import DocumentFileDropZone from '../../components/DocumentFileDropZone';
import SupplementaryDocumentsUploader from '../../components/SupplementaryDocumentsUploader';
import { getSupplementaryDocumentIdsFromMemberRow } from '../../models/memberModel';
import { memberController } from '../../controllers/memberController';
import {
  primaryRoleKeyFromPosition,
  resolveUserRoleFromPosition,
  formatIamRoleLabel,
  isOrgAdminRole,
  isOrgManagerRole,
} from '../../utils/iamRoles';
import {
  PROFILE_TYPES,
  SEX_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  IDENTITY_DOC_TYPES,
  LANGUAGE_OPTIONS,
  NATIONALITY_OPTIONS,
  NATIONALITY_CODE_SET,
} from '../../utils/peopleFormOptions';
import { normalizeToSupportedUiLanguage } from '../../constants/uiLanguages';
import { useProfileShellOutletMerged } from '../profile/ProfileSectionShells';
import { useMemberProfileCompletion } from '../../contexts/MemberProfileCompletionContext';
import {
  isMemberRequiredFieldsComplete,
  needsProfileCompletionBanner,
} from '../../utils/memberProfileCompletion';
import { buildMemberDashboardPersonalStats } from '../../utils/memberDashboardPersonalStats';
import {
  harmonizedMemberUnpaidMonthsDisplay,
  unpaidMonthsCardClasses,
} from '../../utils/contributionArrearsUi';
import { staffMembersBasePath, staffMembersListPath } from '../../utils/portalAdminNav';

const MEMBER_EDIT_DEFAULT_TAB = 'infos_personnels';

const MemberEdit = () => {
  const { t } = useTranslation('common');
  const outletCtx = useProfileShellOutletMerged();
  const profileShellMembre = outletCtx.profileShell === 'membre';
  const { id: routeMemberId } = useParams();
  const id = outletCtx.profileMemberId ?? routeMemberId;
  const navigate = useNavigate();
  const location = useLocation();
  const membersListPath = staffMembersListPath(location.pathname);
  const membersBase = staffMembersBasePath(location.pathname);
  const { error, success } = useToast();
  const { user: authUser } = useAuth();
  const { refresh: refreshProfileCompletion, complete: profileComplete } = useMemberProfileCompletion();
  const currentProjectId = localStorage.getItem('project_id') || null;
  const isOwnLinkedMemberProfile =
    String(authUser?.member_id || authUser?.person_id || '').trim() === String(id || '').trim();
  const needsProfileCompletion =
    isOwnLinkedMemberProfile &&
    (needsProfileCompletionBanner(location.state) || profileComplete === false);

  const memberEditNavTabs = useMemo(
    () =>
      isOwnLinkedMemberProfile
        ? [
            ['infos_personnels', 'Infos personnels'],
            ['contacts', 'Contacts'],
            ['documents', 'Documents'],
          ]
        : [
            ['infos_personnels', 'Infos personnels'],
            ['adhesion', 'Adhésion'],
            ['contacts', 'Contacts'],
            ['cotisations', 'Cotisations'],
            ['documents', 'Documents'],
            ['historique', 'Historique'],
          ],
    [isOwnLinkedMemberProfile]
  );

  const memberEditTabIdSet = useMemo(
    () => new Set(memberEditNavTabs.map(([tid]) => tid)),
    [memberEditNavTabs]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingIdentity, setUploadingIdentity] = useState(false);
  const [activeTab, setActiveTab] = useState(MEMBER_EDIT_DEFAULT_TAB);
  const [detailExtrasLoading, setDetailExtrasLoading] = useState(false);
  const [detailContributions, setDetailContributions] = useState([]);
  const [detailStats, setDetailStats] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [projectProfileTypes, setProjectProfileTypes] = useState(PROFILE_TYPES);
  const [defaultProfileType, setDefaultProfileType] = useState('member');
  const [projectPositions, setProjectPositions] = useState([]);
  /** Compte IAM lié au membre (null si pas encore créé / chargé). */
  const [linkedUser, setLinkedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    join_date: '',
    status: 'active',
    commission: '',
    type: 'member',
    first_name: '',
    last_name: '',
    middle_name: '',
    date_of_birth: '',
    place_of_birth: '',
    sex: '',
    marital_status: '',
    nationality: '',
    profession: '',
    id_number: '',
    identity_document_type: '',
    identity_document_number: '',
    identity_document_expiration_date: '',
    identity_document_file_id: '',
    photo_file_id: '',
    address_line1: '',
    address_line2: '',
    address_city: '',
    address_country: '',
    address_postal_code: '',
    preferred_language: '',
    member_number: '',
    position_id: '',
  });
  const nationalityOptions = useMemo(() => {
    if (!formData.nationality || NATIONALITY_CODE_SET.has(formData.nationality)) {
      return NATIONALITY_OPTIONS;
    }
    return [{ value: formData.nationality, label: formData.nationality }, ...NATIONALITY_OPTIONS];
  }, [formData.nationality]);

  useEffect(() => {
    if (!id) return;
    loadProjectProfileTypes();
    loadMember();
    loadCommissions();
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await positionsAPI.getList();
        if (!cancelled) setProjectPositions(res.data?.positions || []);
      } catch {
        if (!cancelled) setProjectPositions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return;
    if (memberEditTabIdSet.has(h)) {
      setActiveTab((prev) => (h !== prev ? h : prev));
      return;
    }
    navigate(
      { pathname: location.pathname, search: location.search, hash: MEMBER_EDIT_DEFAULT_TAB },
      { replace: true }
    );
    setActiveTab(MEMBER_EDIT_DEFAULT_TAB);
  }, [location.hash, location.pathname, location.search, memberEditTabIdSet, navigate]);

  useEffect(() => {
    if (activeTab !== 'historique' || historyLoaded || !id || isOwnLinkedMemberProfile) return;
    let cancelled = false;
    (async () => {
      try {
        setHistoryLoading(true);
        const res = await peoplePlatformAPI.getHistory(id, { limit: 100, skip: 0 });
        if (cancelled) return;
        const entries = Array.isArray(res?.data?.entries) ? res.data.entries : [];
        setHistoryEntries(entries);
        setHistoryLoaded(true);
      } catch (err) {
        if (!cancelled) {
          console.error('People history:', err);
          setHistoryEntries([]);
          setHistoryLoaded(true);
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, historyLoaded, id, isOwnLinkedMemberProfile]);

  const loadMemberDetailsContext = async () => {
    if (!id) return;
    setDetailExtrasLoading(true);
    try {
      const details = await memberController.fetchMemberDetails(id);
      const { contributions, stats } = details;
      setDetailContributions(contributions);
      setDetailStats(stats);
    } catch (e) {
      console.error(e);
      setDetailContributions([]);
      setDetailStats(null);
    } finally {
      setDetailExtrasLoading(false);
    }
  };

  const loadProjectProfileTypes = async () => {
    try {
      const res = await peoplePlatformAPI.getProfileTypes();
      const list = Array.isArray(res?.data?.profile_types)
        ? res.data.profile_types
            .map((v) => String(v || '').trim().toLowerCase())
            .filter(Boolean)
        : [];
      const normalized = list.length > 0 ? Array.from(new Set(list)) : PROFILE_TYPES;
      const defaultType = normalized[0] || 'member';
      setProjectProfileTypes(normalized);
      setDefaultProfileType(defaultType);
      setFormData((prev) => ({
        ...prev,
        type: prev.type || defaultType,
      }));
    } catch (_err) {
      setProjectProfileTypes(PROFILE_TYPES);
      setDefaultProfileType(PROFILE_TYPES[0] || 'member');
    }
  };

  const selectMemberEditTab = (tid) => {
    setActiveTab(tid);
    navigate(
      { pathname: location.pathname, search: location.search, hash: tid },
      { replace: true }
    );
  };

  const displayNameForStats = useMemo(
    () =>
      [formData.last_name, formData.first_name].filter(Boolean).join(', ') ||
      String(formData.name || '').trim() ||
      'Membre',
    [formData.last_name, formData.first_name, formData.name]
  );

  const cotisationHarmonizedUnpaid = useMemo(() => {
    if (!id || !detailStats) return 0;
    return harmonizedMemberUnpaidMonthsDisplay(
      buildMemberDashboardPersonalStats(
        id,
        detailContributions,
        detailStats,
        displayNameForStats,
        formData.member_number || ''
      )
    );
  }, [id, detailStats, detailContributions, displayNameForStats, formData.member_number]);

  const loadCommissions = async () => {
    try {
      const { rows } = await memberController.fetchCommissions();
      setCommissions(rows);
    } catch (err) {
      console.error('Failed to load commissions:', err);
      setCommissions([]);
    }
  };

  const loadMember = async () => {
    try {
      setLoading(true);
      setHistoryLoaded(false);
      setHistoryEntries([]);
      const { member } = await memberController.fetchMemberForEdit(id);

      let email = member.email || '';
      let resolvedLinkedUser = null;
      try {
        const userResponse = await usersAPI.getByMemberId(id);
        resolvedLinkedUser = userResponse?.data || null;
        if (resolvedLinkedUser?.email) {
          email = resolvedLinkedUser.email;
        }
      } catch (err) {
        console.warn('User not found for member or error loading user:', err);
      }
      setLinkedUser(resolvedLinkedUser);

      setFormData({
        ...member,
        email,
        type: member?.type || defaultProfileType,
        preferred_language: member?.preferred_language
          ? normalizeToSupportedUiLanguage(member.preferred_language)
          : '',
        position_id: resolvedLinkedUser?.position_id
          ? String(resolvedLinkedUser.position_id)
          : '',
      });
      setOtherDocuments(
        getSupplementaryDocumentIdsFromMemberRow(member).map((fid) => ({
          id: fid,
          filename: fid,
        }))
      );
      if (!isOwnLinkedMemberProfile) {
        await loadMemberDetailsContext();
      } else {
        setDetailContributions([]);
        setDetailStats(null);
      }
    } catch (err) {
      error('Erreur lors du chargement du membre');
      console.error(err);
      navigate(profileShellMembre ? '/dashboard' : membersListPath);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isMemberRequiredFieldsComplete(formData, { selfEdit: isOwnLinkedMemberProfile })) {
      if (!String(formData.first_name || '').trim() || !String(formData.last_name || '').trim()) {
        error('Le prenom et le nom sont obligatoires.');
      } else if (!String(formData.email || '').trim()) {
        error("L'email est obligatoire.");
      } else if (!String(formData.phone || '').trim()) {
        error('Le téléphone est obligatoire.');
      } else if (!isOwnLinkedMemberProfile && !String(formData.join_date || '').trim()) {
        error("La date d'adhésion est obligatoire.");
      } else {
        error(t('firstLogin.completeProfileIncomplete'));
      }
      return;
    }
    if (formData.nationality && !NATIONALITY_CODE_SET.has(formData.nationality)) {
      error('La nationalite doit etre un code ISO alpha-2 (ex: RW, BI, CD).');
      return;
    }
    try {
      setSaving(true);
      await memberController.saveMember(
        id,
        { ...formData, other_document_file_ids: otherDocuments.map((d) => d.id) },
        {
          currentProjectId,
          selfEdit: isOwnLinkedMemberProfile,
        }
      );

      // La position projet définie sur la fiche membre est portée par l'utilisateur IAM lié.
      // Si un compte est associé, on synchronise `position_id` + le rôle dérivé.
      if (!isOwnLinkedMemberProfile && linkedUser?.id) {
        const nextPositionId = String(formData.position_id || '').trim();
        const prevPositionId = String(linkedUser.position_id || '').trim();
        if (nextPositionId !== prevPositionId) {
          const isTechnicalIam =
            isOrgAdminRole(linkedUser.role) ||
            isOrgManagerRole(linkedUser.role) ||
            ['admin_project', 'saas_admin'].includes(String(linkedUser.role || '').toLowerCase());
          const derivedRole = nextPositionId
            ? resolveUserRoleFromPosition(nextPositionId, projectPositions, linkedUser.role || 'participant')
            : null;
          const userUpdate = { position_id: nextPositionId };
          if (!isTechnicalIam && derivedRole) {
            userUpdate.role = derivedRole;
          }
          try {
            await usersAPI.update(linkedUser.id, userUpdate);
          } catch (userErr) {
            console.warn('Member position saved but linked user sync failed:', userErr);
            error("La position a été enregistrée mais la synchronisation du compte IAM a échoué.");
          }
        }
      }

      success('Membre mis à jour avec succès');
      if (needsProfileCompletion && isOwnLinkedMemberProfile) {
        const ok = await refreshProfileCompletion();
        if (ok) {
          navigate('/dashboard', { replace: true });
        } else {
          error(t('firstLogin.completeProfileIncomplete'));
        }
        return;
      }
      navigate(isOwnLinkedMemberProfile ? '/dashboard' : `${membersBase}/${id}`);
    } catch (err) {
      // Surface le détail serveur (422 Pydantic, 409 contact, etc.).
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = 'Erreur lors de la mise à jour du membre';
      if (detail) {
        if (Array.isArray(detail)) {
          msg = detail
            .map((d) => {
              const field = Array.isArray(d?.loc) ? d.loc.slice(1).join('.') : 'champ';
              return `${field} : ${d?.msg || d?.message || 'invalide'}`;
            })
            .join(' | ');
        } else if (typeof detail === 'string') {
          msg = detail;
        } else if (typeof detail === 'object') {
          msg = detail.message || JSON.stringify(detail);
        }
      } else if (err?.message) {
        msg = err.message;
      }
      error(status ? `${msg} (HTTP ${status})` : msg);
      console.error('Member save error:', err?.response?.data || err);
      console.error('Member save request payload:', err?.config?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (needsProfileCompletion && isOwnLinkedMemberProfile) {
      return;
    }
    navigate(isOwnLinkedMemberProfile ? '/profile' : `${membersBase}/${id}`);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'FBU',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);

  const formatDateShort = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return String(dateString);
    }
  };

  const uploadFileAndSetField = async (file, fieldName, label) => {
    if (!file) return;
    const setUploading =
      fieldName === 'photo_file_id' ? setUploadingPhoto : setUploadingIdentity;
    try {
      setUploading(true);
      const uploaded = await filesPlatformAPI.upload(file);
      const fileId = uploaded?.data?.id;
      if (!fileId) throw new Error('ID fichier manquant');
      setFormData((prev) => ({ ...prev, [fieldName]: String(fileId) }));
      success(`${label} téléversé avec succès`);
    } catch (err) {
      error(
        err?.response?.data?.detail ||
          err?.message ||
          `Erreur lors du téléversement ${label.toLowerCase()}`
      );
    } finally {
      setUploading(false);
    }
  };

  if (!id) {
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center bg-gray-50 px-4 py-12">
        <p className="text-center text-gray-700">Identifiant membre manquant.</p>
        <Link
          to={profileShellMembre ? '/profile' : membersListPath}
          className="mt-4 font-semibold text-entrea-royal hover:underline"
        >
          Retour au profil
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white px-3 py-2.5 shadow-sm md:px-6 md:py-4">
        {!needsProfileCompletion ? (
        <button
          type="button"
          onClick={handleCancel}
          className="flex min-h-[40px] items-center text-slate-600 transition-colors hover:text-gray-900 md:min-h-[44px]"
        >
          <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium md:text-base">
            {profileShellMembre ? 'Retour au profil' : 'Retour'}
          </span>
        </button>
        ) : null}
      </div>

      <div className="mx-auto max-w-6xl px-2.5 py-3 md:px-6 md:py-8">
        <div className="mb-3 md:mb-6">
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm md:rounded-lg md:p-6">
            <h1 className="text-lg font-bold tracking-tight text-[#0f3350] sm:text-2xl md:text-3xl md:text-gray-900">
              {needsProfileCompletion
                ? t('firstLogin.completeProfileTitle')
                : profileShellMembre
                  ? 'Modifier mon profil'
                  : 'Modifier le membre'}
            </h1>
            {needsProfileCompletion ? (
              <p className="mt-2 text-xs text-slate-600 md:mt-3 md:text-sm">{t('firstLogin.completeProfileHint')}</p>
            ) : null}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm md:rounded-lg md:p-6">
          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-6">
            <div className="border-b border-slate-100">
              <nav className="flex gap-1.5 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Sections édition membre">
                {memberEditNavTabs.map(([tid, label]) => (
                  <button
                    key={tid}
                    type="button"
                    onClick={() => selectMemberEditTab(tid)}
                    className={`whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-semibold transition md:px-3 md:py-2 md:text-sm md:font-medium ${
                      activeTab === tid
                        ? 'bg-[#2f6fd4] text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            {activeTab === 'historique' && !isOwnLinkedMemberProfile && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-6">
                <h3 className="text-base font-bold text-gray-900 mb-3">Historique People</h3>
                <p className="mb-4 text-xs text-gray-500">Journal des changements sur la fiche People.</p>
                {historyLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-entrea-royal" />
                    Chargement…
                  </div>
                )}
                {!historyLoading && historyEntries.length === 0 && (
                  <p className="text-sm text-gray-500">Aucune entrée d&apos;historique métier pour cette fiche.</p>
                )}
                {!historyLoading && historyEntries.length > 0 && (
                  <ul className="space-y-3">
                    {historyEntries.map((entry) => (
                      <li key={entry._id} className="text-sm border-l-2 border-entrea-royal/40 pl-3 py-1">
                        <span className="font-medium text-gray-900">{entry.action}</span>
                        <span className="text-gray-400 mx-2">·</span>
                        <span className="text-gray-500">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString('fr-FR') : '—'}
                        </span>
                        {entry.actor_user_id && (
                          <p className="text-xs text-gray-500 mt-1">
                            Acteur : <span className="font-mono">{entry.actor_user_id}</span>
                          </p>
                        )}
                        {entry.fields_changed?.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">{entry.fields_changed.join(', ')}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {activeTab === 'cotisations' && !isOwnLinkedMemberProfile && (
              <div className="space-y-4">
                {detailExtrasLoading && (
                  <div className="flex items-center gap-2 py-4 text-sm text-gray-600">
                    <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-entrea-royal" />
                    Chargement des cotisations…
                  </div>
                )}
                <p className="text-sm text-gray-600">
                  Aperçu des cotisations pour ce membre (lecture seule).{' '}
                  <Link to={`${membersBase}/${id}`} className="text-entrea-royal font-medium hover:underline">
                    Ouvrir la fiche détail
                  </Link>
                  .
                </p>
                {!detailExtrasLoading && detailStats && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                      <p className="text-xs font-medium text-green-800">{t('member.cotisationTotalPaid')}</p>
                      <p className="text-xl font-bold text-green-900">
                        {formatCurrency(detailStats.total_contributions || 0)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <p className="text-xs font-medium text-blue-800">{t('member.cotisationPaidMonths')}</p>
                      <p className="text-[11px] text-blue-700/90">{t('member.cotisationPaidMonthsHint')}</p>
                      <p className="text-xl font-bold text-blue-900">{detailStats.paid_months || 0}</p>
                    </div>
                    {(() => {
                      const tone = unpaidMonthsCardClasses(cotisationHarmonizedUnpaid);
                      return (
                        <div className={`rounded-lg border p-4 ${tone.border} ${tone.bg}`}>
                          <p className={`text-xs font-medium ${tone.label}`}>{t('member.cotisationUnpaidMonths')}</p>
                          <p className={`text-xl font-bold ${tone.value}`}>{cotisationHarmonizedUnpaid}</p>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {detailStats?.last_payment_date && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
                    <span className="text-gray-600">{t('member.cotisationLastPayment')} : </span>
                    <span className="font-semibold text-gray-900">{detailStats.last_payment_date}</span>
                  </div>
                )}
                {!detailExtrasLoading && !detailStats && (
                  <p className="text-sm text-gray-500">{t('member.cotisationNoAggregatedStats')}</p>
                )}
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-800">Lignes de cotisation</div>
                  {!detailExtrasLoading && detailContributions.length === 0 && (
                    <p className="p-4 text-sm text-gray-500">Aucune cotisation enregistrée.</p>
                  )}
                  {!detailExtrasLoading && detailContributions.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 text-left text-xs uppercase text-gray-600">
                          <tr>
                            <th className="px-4 py-2">Période</th>
                            <th className="px-4 py-2">Montant</th>
                            <th className="px-4 py-2">Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailContributions.map((c) => (
                            <tr key={c.id || `${c.month}-${c.amount}`} className="border-t border-gray-200">
                              <td className="px-4 py-2 whitespace-nowrap">
                                {c.month || formatDateShort(c.created_at)}
                              </td>
                              <td className="px-4 py-2">{formatCurrency(c.amount)}</td>
                              <td className="px-4 py-2">{String(c.status || '—')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(activeTab === 'infos_personnels' ||
              (activeTab === 'adhesion' && !isOwnLinkedMemberProfile) ||
              activeTab === 'contacts' ||
              activeTab === 'documents') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {activeTab === 'infos_personnels' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                        placeholder="Prénom"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                        placeholder="Nom"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Post-nom</label>
                      <input
                        type="text"
                        value={formData.middle_name}
                        onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet (généré)</label>
                      <input
                        type="text"
                        value={[formData.first_name, formData.middle_name, formData.last_name].filter(Boolean).join(' ')}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date de naissance</label>
                      <input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Lieu de naissance</label>
                      <input
                        type="text"
                        value={formData.place_of_birth}
                        onChange={(e) => setFormData({ ...formData, place_of_birth: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sexe</label>
                      <select
                        value={formData.sex}
                        onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        {SEX_OPTIONS.map((opt) => (
                          <option key={opt.value || 'empty'} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">État civil</label>
                      <select
                        value={formData.marital_status}
                        onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        {MARITAL_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value || 'empty'} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nationalité</label>
                      <select
                        value={formData.nationality}
                        onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        {nationalityOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Profession</label>
                      <input
                        type="text"
                        value={formData.profession}
                        onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Langue de l&apos;interface
                      </label>
                      <select
                        value={formData.preferred_language}
                        onChange={(e) => setFormData({ ...formData, preferred_language: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        {LANGUAGE_OPTIONS.map((opt) => (
                          <option key={opt.value || 'empty'} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Langues disponibles dans l&apos;application (traductions chargées).
                      </p>
                    </div>
                  </>
                )}

                {activeTab === 'adhesion' && !isOwnLinkedMemberProfile && (
                  <>
                    {formData.member_number ? (
                      <div className="md:col-span-2 rounded-lg border border-entrea-sky/50 bg-entrea-panel/40 px-4 py-3">
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                          N° membre (attribué automatiquement)
                        </label>
                        <p className="text-xl font-bold font-mono tracking-wide text-entrea-royal">{formData.member_number}</p>
                      </div>
                    ) : null}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date d&apos;adhésion *</label>
                      <input
                        type="date"
                        value={formData.join_date}
                        onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        <option value="active">Actif</option>
                        <option value="inactive">Inactif</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Commission (groupe)</label>
                      <select
                        value={formData.commission || ''}
                        onChange={(e) => setFormData({ ...formData, commission: e.target.value || '' })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        <option value="">Aucun groupe</option>
                        {commissions
                          .filter((c) => c && c.name)
                          .map((c) => (
                            <option key={c.id || c.name} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Position projet
                      </label>
                      <select
                        value={formData.position_id || ''}
                        onChange={(e) => setFormData({ ...formData, position_id: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        <option value="">— Aucune —</option>
                        {projectPositions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      {(() => {
                        const pid = String(formData.position_id || '').trim();
                        if (!pid) {
                          return (
                            <p className="mt-1 text-xs text-gray-500">
                              Aucune position définie — l’utilisateur lié garde son rôle technique actuel.
                            </p>
                          );
                        }
                        const pos = projectPositions.find((p) => String(p?.id || '').trim() === pid);
                        const primary = primaryRoleKeyFromPosition(pos);
                        if (!primary) {
                          return (
                            <p className="mt-1 text-xs text-amber-700">
                              Cette position n’a aucune clé de rôle — le rôle de l’utilisateur ne sera pas modifié.
                            </p>
                          );
                        }
                        return (
                          <p className="mt-1 text-xs text-gray-600">
                            À l’enregistrement, l’utilisateur lié reçoit le rôle{' '}
                            <span className="font-semibold text-entrea-royal">
                              {formatIamRoleLabel(primary)}
                            </span>{' '}
                            <span className="font-mono text-gray-500">({primary})</span>.
                          </p>
                        );
                      })()}
                      {!linkedUser?.id ? (
                        <p className="mt-1 text-xs text-amber-700">
                          Aucun compte IAM lié pour ce membre — la position ne s’appliquera qu’après création du compte.
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type de profil</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        {projectProfileTypes.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Type par défaut du projet : <span className="font-semibold">{defaultProfileType}</span>
                      </p>
                    </div>
                  </>
                )}

                {activeTab === 'contacts' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                        placeholder="email@example.com"
                      />
                      <p className="mt-1 text-xs text-entrea-royal">
                        Synchronisé avec l&apos;utilisateur IAM associé s&apos;il existe.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                        placeholder="+250788123456"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Adresse ligne 1</label>
                      <input
                        type="text"
                        value={formData.address_line1}
                        onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Adresse ligne 2</label>
                      <input
                        type="text"
                        value={formData.address_line2}
                        onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                      <input
                        type="text"
                        value={formData.address_city}
                        onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
                      <input
                        type="text"
                        value={formData.address_country}
                        onChange={(e) => setFormData({ ...formData, address_country: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
                      <input
                        type="text"
                        value={formData.address_postal_code}
                        onChange={(e) => setFormData({ ...formData, address_postal_code: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                  </>
                )}

                {activeTab === 'documents' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Numéro de pièce</label>
                      <input
                        type="text"
                        value={formData.identity_document_number}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            identity_document_number: e.target.value,
                            id_number: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type de pièce</label>
                      <select
                        value={formData.identity_document_type}
                        onChange={(e) => setFormData({ ...formData, identity_document_type: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        {IDENTITY_DOC_TYPES.map((opt) => (
                          <option key={opt.value || 'empty'} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date d&apos;expiration</label>
                      <input
                        type="date"
                        value={formData.identity_document_expiration_date}
                        onChange={(e) =>
                          setFormData({ ...formData, identity_document_expiration_date: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="block text-sm font-medium text-gray-700 mb-2">Photo</span>
                        <DocumentFileDropZone
                          variant="photo"
                          disabled={saving}
                          uploading={uploadingPhoto}
                          title="Portrait du membre"
                          hint="Formats image courants (JPG, PNG, WebP…)."
                          onFileSelected={(file) => uploadFileAndSetField(file, 'photo_file_id', 'Photo')}
                          onInvalidFile={(msg) => error(msg)}
                        />
                      </div>
                      <div>
                        <span className="block text-sm font-medium text-gray-700 mb-2">Pièce d&apos;identité</span>
                        <DocumentFileDropZone
                          variant="identity"
                          disabled={saving}
                          uploading={uploadingIdentity}
                          title="Scan ou photo de la pièce"
                          hint="Image ou PDF."
                          onFileSelected={(file) =>
                            uploadFileAndSetField(file, 'identity_document_file_id', 'Pièce')
                          }
                          onInvalidFile={(msg) => error(msg)}
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <SupplementaryDocumentsUploader
                        documents={otherDocuments}
                        onDocumentsChange={setOtherDocuments}
                        disabled={saving}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="sticky bottom-0 z-10 -mx-3 mt-2 border-t border-slate-200 bg-white/95 px-3 py-2.5 backdrop-blur md:static md:mx-0 md:mt-0 md:border-t md:border-gray-200 md:bg-transparent md:px-0 md:pt-6 md:backdrop-blur-none flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 md:gap-4 pb-[max(0.625rem,env(safe-area-inset-bottom,0px))] md:pb-0">
              <button
                type="button"
                onClick={handleCancel}
                className="min-h-[44px] px-4 py-2 border border-gray-300 text-gray-700 rounded-xl md:rounded-lg hover:bg-gray-50 transition font-medium text-sm"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="min-h-[48px] px-4 py-2 bg-[#113a5b] md:bg-entrea-royal text-white rounded-xl md:rounded-lg hover:bg-entrea-navy transition font-semibold md:font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm shadow-sm md:shadow-none"
              >
                {saving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 md:mr-3 h-4 w-4 md:h-5 md:w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span className="hidden sm:inline">Enregistrement...</span>
                    <span className="sm:hidden">En cours...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">
                      {needsProfileCompletion ? 'Enregistrer et continuer' : 'Enregistrer les modifications'}
                    </span>
                    <span className="sm:hidden">
                      {needsProfileCompletion ? 'Continuer' : 'Enregistrer'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MemberEdit;
